import { useMemo } from 'react'
import { GEAR_DEFINITIONS } from './gear'
import { DrawingElement, GearElement } from './types'
import { ROPE_ROLES } from './rope-roles'
import { getGearSubtype } from './gear-subtypes'

type LegendProps = {
  elements: DrawingElement[]
}

type GearScheduleEntry = {
  legendNumber: number
  gearType: string
  subtype: string | undefined
  label: string
  color: string
  details: string
}

function buildGearEntry(element: GearElement): GearScheduleEntry {
  const def = GEAR_DEFINITIONS[element.gearType]
  const st = getGearSubtype(element.subtype)
  const displayLabel = element.label?.trim() || def.label
  const color = st?.color ?? def.color
  const details = st ? st.label : def.label
  return {
    legendNumber: element.legendNumber ?? 0,
    gearType: element.gearType,
    subtype: element.subtype,
    label: displayLabel,
    color,
    details,
  }
}

/**
 * Numbered legend / equipment schedule shown over the canvas. Each gear
 * element gets a row keyed by its stable legend number. Rope roles are
 * listed separately below.
 */
export function RiggingLegend({ elements }: LegendProps) {
  const { roles, gearEntries } = useMemo(() => {
    const roleSet = new Set<string>()
    const entries: GearScheduleEntry[] = []
    for (const element of elements) {
      if (element.kind === 'rope') {
        if (element.role) roleSet.add(element.role)
      } else if (element.kind === 'gear') {
        entries.push(buildGearEntry(element))
      }
    }
    entries.sort((a, b) => a.legendNumber - b.legendNumber)
    return {
      roles: ROPE_ROLES.filter((style) => roleSet.has(style.role)),
      gearEntries: entries,
    }
  }, [elements])

  if (roles.length === 0 && gearEntries.length === 0) return null

  return (
    <div className="pointer-events-auto absolute right-4 top-[84px] z-20 flex max-h-[calc(100vh-140px)] w-56 flex-col rounded-lg border border-slate-200 bg-white/95 p-3 text-xs shadow-sm backdrop-blur">
      <div className="mb-2 shrink-0 text-[10px] font-bold uppercase tracking-wide text-slate-500">Equipment Schedule</div>
      <div className="min-h-0 overflow-y-auto">
        {gearEntries.length > 0 && (
          <div className="mb-2">
            <table className="w-full text-left">
              <thead>
                <tr className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">
                  <th className="pb-1 pr-1 w-5">#</th>
                  <th className="pb-1 pr-1">Item</th>
                  <th className="pb-1">Type</th>
                </tr>
              </thead>
              <tbody>
                {gearEntries.map((entry) => (
                  <tr key={`${entry.legendNumber}-${entry.gearType}`} className="text-slate-700">
                    <td className="pr-1 py-0.5 align-top">
                      <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-blue-800 text-[8px] font-bold text-white">
                        {entry.legendNumber}
                      </span>
                    </td>
                    <td className="pr-1 py-0.5 align-top font-medium">{entry.label}</td>
                    <td className="py-0.5 align-top">
                      <span className="flex items-center gap-1">
                        <span className="inline-block h-2 w-2 shrink-0 rounded-sm" style={{ background: entry.color }} />
                        <span className="text-slate-500">{entry.details}</span>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {roles.length > 0 && (
          <div>
            <div className="mb-1 text-[9px] font-semibold uppercase tracking-wide text-slate-400">Ropes</div>
            <ul className="space-y-1">
              {roles.map((style) => (
                <li key={style.role} className="flex items-center gap-2 text-slate-700">
                  <span
                    className="inline-block h-0 w-5 shrink-0"
                    style={{ borderTop: `3px ${style.dash ? 'dashed' : 'solid'} ${style.color}` }}
                  />
                  {style.label}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}
